import React, { useState, useEffect, useRef } from 'react';
import './ProductImageGallery.css';
import { useTranslation } from 'react-i18next';
import swal from 'sweetalert';

import { IoMdClose, IoMdAdd, IoMdTrash } from 'react-icons/io';
import { FaImages, FaStar, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { MdDragIndicator } from 'react-icons/md';

import {
    get_product_images,
    upload_product_image,
    delete_product_image,
    reorder_product_images,
    set_principal_image
} from '../../context/Services/Settings';

const ProductImageGallery = ({ stockId, readOnly = false, onPrincipalChange }) => {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [draggedIndex, setDraggedIndex] = useState(null);

    // Fetch images
    const fetchImages = async () => {
        if (!stockId) return;

        setLoading(true);
        try {
            const res = await get_product_images(stockId);
            const fetchedImages = res?.data || [];
            setImages(fetchedImages);

            // Notify parent of principal image
            const principalImg = fetchedImages.find(img => img.is_principal) || fetchedImages[0];
            if (onPrincipalChange) {
                onPrincipalChange(principalImg || null);
            }
        } catch (e) {
            console.error('Failed to fetch images:', e);
        } finally {
            setLoading(false);
        }
    };

    // Upload new image
    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            swal('Error', t('Veuillez sélectionner une image'), 'error');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            swal('Error', t('La taille maximale est de 5MB'), 'error');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);

            await upload_product_image(stockId, formData);
            await fetchImages();
            swal('Success', t('Image ajoutée avec succès'), 'success');
        } catch (e) {
            console.error('Upload failed:', e);
            swal('Error', e?.response?.data?.message || t('Échec de l\'upload'), 'error');
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Delete image
    const handleDelete = async (imageId) => {
        const willDelete = await swal({
            title: t('Êtes-vous sûr ?'),
            text: t('Cette image sera supprimée définitivement'),
            icon: 'warning',
            buttons: true,
            dangerMode: true,
        });

        if (willDelete) {
            try {
                await delete_product_image(imageId);
                await fetchImages();
                setSelectedImage(null);
            } catch (e) {
                console.error('Delete failed:', e);
                swal('Error', t('Échec de la suppression'), 'error');
            }
        }
    };

    // Set image as principal
    const handleSetPrincipal = async (imageId) => {
        try {
            const res = await set_principal_image(imageId);
            const updatedImages = res?.data || [];
            setImages(updatedImages);

            // Notify parent of new principal image
            const principalImg = updatedImages.find(img => img.is_principal);
            if (onPrincipalChange) {
                onPrincipalChange(principalImg || null);
            }

            swal('Success', t('Image principale mise à jour'), 'success');
        } catch (e) {
            console.error('Set principal failed:', e);
            swal('Error', t('Échec de la mise à jour'), 'error');
        }
    };

    // Handle drag start
    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    // Handle drag over
    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newImages = [...images];
        const draggedImage = newImages[draggedIndex];
        newImages.splice(draggedIndex, 1);
        newImages.splice(index, 0, draggedImage);

        setImages(newImages);
        setDraggedIndex(index);
    };

    // Handle drag end - save new order
    const handleDragEnd = async () => {
        if (draggedIndex === null) return;

        setDraggedIndex(null);

        try {
            const imageIds = images.map(img => img._id);
            await reorder_product_images(stockId, imageIds);
        } catch (e) {
            console.error('Reorder failed:', e);
            // Refresh to get original order
            await fetchImages();
        }
    };

    // Navigate lightbox
    const handlePrevImage = () => {
        if (selectedImage === null) return;
        const currentIndex = images.findIndex(img => img._id === selectedImage._id);
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
        setSelectedImage(images[prevIndex]);
    };

    const handleNextImage = () => {
        if (selectedImage === null) return;
        const currentIndex = images.findIndex(img => img._id === selectedImage._id);
        const nextIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
        setSelectedImage(images[nextIndex]);
    };

    useEffect(() => {
        fetchImages();
    }, [stockId]);

    return (
        <div className="product_image_gallery">
            <div className="gallery_header">
                <div className="gallery_title">
                    <FaImages className="gallery_icon" />
                    <h4>{t('Galerie d\'images')}</h4>
                    <span className="image_count">{images.length} {t('images')}</span>
                </div>

                {!readOnly && (
                    <div className="gallery_actions">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleUpload}
                            accept="image/*"
                            style={{ display: 'none' }}
                        />
                        <button
                            className="upload_btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <span className="uploading_text">{t('Upload...')}</span>
                            ) : (
                                <>
                                    <IoMdAdd />
                                    <span>{t('Ajouter')}</span>
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="gallery_loading">
                    <div className="spinner"></div>
                    <span>{t('Chargement...')}</span>
                </div>
            ) : images.length === 0 ? (
                <div className="gallery_empty">
                    <FaImages size={48} color="#ddd" />
                    <p>{t('Aucune image')}</p>
                    {!readOnly && (
                        <button
                            className="upload_btn_large"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <IoMdAdd />
                            <span>{t('Ajouter la première image')}</span>
                        </button>
                    )}
                </div>
            ) : (
                <div className="gallery_grid">
                    {images.map((image, index) => (
                        <div
                            key={image._id}
                            className={`gallery_item ${draggedIndex === index ? 'dragging' : ''}`}
                            draggable={!readOnly}
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                        >
                            {image.is_principal && (
                                <div className="primary_badge">
                                    <FaStar size={10} />
                                    <span>{t('Principal')}</span>
                                </div>
                            )}

                            <img
                                src={image.image_path}
                                alt={`Product ${index + 1}`}
                                onClick={() => setSelectedImage(image)}
                            />

                            {!readOnly && (
                                <div className="image_overlay">
                                    <MdDragIndicator className="drag_handle" />
                                    {!image.is_principal && (
                                        <button
                                            className="set_principal_btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSetPrincipal(image._id);
                                            }}
                                            title={t('Définir comme image principale')}
                                        >
                                            <FaStar />
                                        </button>
                                    )}
                                    <button
                                        className="delete_image_btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(image._id);
                                        }}
                                    >
                                        <IoMdTrash />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Lightbox */}
            {selectedImage && (
                <div className="lightbox" onClick={() => setSelectedImage(null)}>
                    <button className="lightbox_close">
                        <IoMdClose size={24} />
                    </button>

                    <button
                        className="lightbox_nav lightbox_prev"
                        onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
                    >
                        <FaArrowLeft />
                    </button>

                    <div className="lightbox_content" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={selectedImage.image_path}
                            alt="Product"
                        />
                    </div>

                    <button
                        className="lightbox_nav lightbox_next"
                        onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                    >
                        <FaArrowRight />
                    </button>

                    <div className="lightbox_counter">
                        {images.findIndex(img => img._id === selectedImage._id) + 1} / {images.length}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductImageGallery;
